import MobileCard from 'app/pages/mobile/pages/mobile-card/MobileCard';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { GetCardsPayload } from 'CDK_API';
import { CARD_DIMENSIONS } from 'app/pages/mobile/utils/constants';
import { useNavigate } from 'react-router-dom';

interface MobileCardCarouselProps {
  cards: GetCardsPayload[];
  onCardClick?: (paymentCardId: string) => void;
  onCardChange?: (cardIndex: number) => void;
  loading?: boolean;
  error?: string | null;
}

const MobileCardCarousel: React.FC<MobileCardCarouselProps> = ({
  cards,
  onCardClick,
  onCardChange,
  loading = false,
  error = null,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const handleCardNavigation = useCallback(
    (paymentCardId: string, card: GetCardsPayload) => {
      if (onCardClick) {
        onCardClick(paymentCardId);
      } else {
        // Check if card needs activation
        if (card.status === 'ACTIVATION_REQUIRED') {
          navigate(`/card/${paymentCardId}/activate`, { replace: true });
        } else {
          navigate(`/card/${paymentCardId}`, { replace: true });
        }
      }
    },
    [onCardClick, navigate]
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [swipeDirection, setSwipeDirection] = useState<'horizontal' | 'vertical' | 'undetermined'>(
    'undetermined'
  );
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate individual card transforms for gallery-style sliding with peek
  const getCardTransform = useCallback(
    (cardIndex: number) => {
      const containerWidth = containerRef.current?.offsetWidth || 1;
      const dragOffsetPercent = isDragging ? (dragOffset / containerWidth) * 100 : 0;

      // Peek amount - how much of adjacent cards to show (in percentage)
      const peekPercent = -2;

      // Calculate base position with peek effect
      const cardOffset = cardIndex - currentIndex;
      let baseOffset;

      if (cardOffset === 0) {
        // Current card: centered
        baseOffset = 0;
      } else if (cardOffset === -1) {
        // Previous card: positioned to show peek on the left
        baseOffset = -(100 - peekPercent);
      } else if (cardOffset === 1) {
        // Next card: positioned to show peek on the right
        baseOffset = 100 - peekPercent;
      } else {
        // Cards further away: completely off-screen
        baseOffset = cardOffset * 100;
      }

      // Apply drag offset to create smooth sliding effect
      const finalOffset = baseOffset - dragOffsetPercent;

      return `translateX(${finalOffset}%)`;
    },
    [currentIndex, dragOffset, isDragging]
  );

  // Navigate to specific card
  const handleChangeCard = useCallback(
    (index: number) => {
      setCurrentIndex(index);
      setDragOffset(0);
      onCardChange?.(index);
    },
    [onCardChange]
  );

  // Set up native event listeners to control passive behavior
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Native touch event handlers with explicit passive control
    const nativeTouchStart = (e: TouchEvent) => {
      setStartX(e.touches[0].clientX);
      setStartY(e.touches[0].clientY);
      setStartTime(Date.now());
      setDragOffset(0);
      setSwipeDirection('undetermined');
      setIsDragging(false);
    };

    const nativeTouchMove = (e: TouchEvent) => {
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const deltaX = Math.abs(currentX - startX);
      const deltaY = Math.abs(currentY - startY);

      // Determine swipe direction if not yet determined
      if (swipeDirection === 'undetermined') {
        const minMovement = 10; // Minimum pixels to determine direction

        if (deltaX > minMovement || deltaY > minMovement) {
          if (deltaX > deltaY * 1.5) {
            // Horizontal swipe - ratio ensures it's clearly horizontal
            setSwipeDirection('horizontal');
            setIsDragging(true);
            e.preventDefault(); // Now prevent default for horizontal swipes
          } else {
            // Vertical swipe or unclear - let browser handle it
            setSwipeDirection('vertical');
            return; // Exit early, don't interfere with vertical scrolling
          }
        } else {
          return; // Not enough movement yet
        }
      }

      // Only handle if we've determined this is a horizontal swipe
      if (swipeDirection !== 'horizontal' || !isDragging) return;

      // Prevent default for horizontal swipes only
      e.preventDefault();

      let offset = currentX - startX;

      // Add resistance at boundaries
      const containerWidth = containerRef.current?.offsetWidth || 1;
      const maxDrag = containerWidth * 0.6;

      // Apply dampening at boundaries
      if (currentIndex === 0 && offset > 0) {
        offset = offset * 0.3;
      } else if (currentIndex === cards.length - 1 && offset < 0) {
        offset = offset * 0.3;
      }

      // Limit maximum drag distance
      offset = Math.max(-maxDrag, Math.min(maxDrag, offset));

      setDragOffset(offset);
    };

    const nativeTouchEnd = () => {
      // Only process if this was a horizontal swipe that we were handling
      if (!isDragging || swipeDirection !== 'horizontal') {
        // Reset state for next interaction
        setIsDragging(false);
        setDragOffset(0);
        setSwipeDirection('undetermined');
        return;
      }

      setIsDragging(false);
      const endTime = Date.now();
      const timeDiff = Math.max(endTime - startTime, 1);
      const velocity = Math.abs(dragOffset) / timeDiff;

      // More reasonable thresholds
      const containerWidth = containerRef.current?.offsetWidth || 1;
      const distanceThreshold = containerWidth * 0.15;
      const velocityThreshold = 0.3;

      const shouldChangeCard = Math.abs(dragOffset) > distanceThreshold || velocity > velocityThreshold;

      let newIndex = currentIndex;

      if (shouldChangeCard) {
        if (dragOffset > 0) {
          newIndex = Math.max(0, currentIndex - 1);
        } else {
          newIndex = Math.min(cards.length - 1, currentIndex + 1);
        }
      }

      setCurrentIndex(newIndex);
      setDragOffset(0);
      setSwipeDirection('undetermined');
      onCardChange?.(newIndex);
    };

    // Add native event listeners with explicit passive settings
    container.addEventListener('touchstart', nativeTouchStart, { passive: true });
    container.addEventListener('touchmove', nativeTouchMove, { passive: false }); // Non-passive so we can preventDefault
    container.addEventListener('touchend', nativeTouchEnd, { passive: true });

    // Cleanup
    return () => {
      container.removeEventListener('touchstart', nativeTouchStart);
      container.removeEventListener('touchmove', nativeTouchMove);
      container.removeEventListener('touchend', nativeTouchEnd);
    };
  }, [
    startX,
    startY,
    startTime,
    swipeDirection,
    isDragging,
    dragOffset,
    currentIndex,
    cards.length,
    onCardChange,
  ]);

  if (loading) {
    return <MobileCard variant="loading" />;
  }

  if (error) {
    return <MobileCard variant="error" error={error} />;
  }

  if (!cards || cards.length === 0) {
    return <MobileCard variant="empty" />;
  }

  /* Single Card */
  if (cards.length === 1) {
    const card = cards[0];

    return (
      <MobileCard
        paymentCard={card}
        index={0}
        isActive={true}
        isPeekCard={false}
        isDragging={false}
        onCardClick={(paymentCardId) => handleCardNavigation(paymentCardId, card)}
      />
    );
  }

  /* Carousel */
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(2),
        flexShrink: 0, // Prevent the carousel from being squished
        minHeight: 'fit-content', // Ensure it maintains its natural height
      }}
    >
      {/* Carousel Container */}
      <Box
        ref={containerRef}
        aria-label={`${cards.length} cards. Swipe to view more.`}
        tabIndex={0}
        onKeyDown={(e) => {
          switch (e.key) {
            case 'ArrowLeft':
              e.preventDefault();
              if (currentIndex > 0) {
                handleChangeCard(currentIndex - 1);
              }
              break;
            case 'ArrowRight':
              e.preventDefault();
              if (currentIndex < cards.length - 1) {
                handleChangeCard(currentIndex + 1);
              }
              break;
            case 'Home':
              e.preventDefault();
              handleChangeCard(0);
              break;
            case 'End':
              e.preventDefault();
              handleChangeCard(cards.length - 1);
              break;
          }
        }}
        sx={{
          width: CARD_DIMENSIONS.viewportWidth,
          maxWidth: CARD_DIMENSIONS.maxWidth,
          aspectRatio: String(CARD_DIMENSIONS.aspectRatio),
          overflow: 'visible', // Allow peek cards to show beyond container
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          KhtmlUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
          display: 'flex',
          justifyContent: 'center',
          position: 'relative', // Add relative positioning for absolute children
          '&:focus': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: '2px',
          },
        }}
      >
        {cards.map((card, index) => {
          // Skip null/undefined cards
          if (!card || !card.paymentCardId) return null;

          const isActive = index === currentIndex;
          const isPeekCard = Math.abs(index - currentIndex) === 1;
          const isVisible = Math.abs(index - currentIndex) <= 1; // Only render current and adjacent cards

          if (!isVisible) return null;

          return (
            <Box
              key={card.paymentCardId}
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                display: 'flex',
                justifyContent: 'center',
                pointerEvents: isActive ? 'auto' : 'none', // Only center card is clickable
              }}
            >
              <MobileCard
                paymentCard={card}
                index={index}
                isActive={isActive}
                isPeekCard={isPeekCard}
                isDragging={isDragging}
                getCardTransform={getCardTransform}
                onCardClick={(paymentCardId) => handleCardNavigation(paymentCardId, card)}
                onCardChange={onCardChange}
                setCurrentIndex={setCurrentIndex}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default MobileCardCarousel;
